import { ContentHeader, FormContainer, Input, InputProps } from '@app/shared';
import { Brand } from '@app/models';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useCreateBrand, useUpdateBrand, useRefreshBrands } from '@app/sdk';
import BrandOpeningHours from './BrandOpeningHours';

export function BrandEditForm({ brand }: { brand: Brand }) {
  var isUpdateFlow = false;
  const formContext = useForm<Brand>({ values: brand });

  if (brand && brand.id) {
    isUpdateFlow = true;
  }
  const refreshBrands = useRefreshBrands();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateBrand();
  const { mutateAsync: create, isPending: isCreating } = useCreateBrand();

  async function SaveBrand(model: Brand): Promise<string> {
    const request = { ...model };

    if (request.contactNumbers.length == 0) {
      alert('ContactNumbers needs at least one item.');
      return;
    }

    if (request.openingHours.collection.length == 0) {
      request.openingHours.collection = null;
    }
    if (request.openingHours.customerService.length == 0) {
      request.openingHours.customerService = null;
    }

    if (request.openingHours.clockFormat == '--Select clock format--') {
      request.openingHours.clockFormat = null;
    }

    if (
      request.openingHours.collection == null &&
      request.openingHours.customerService == null &&
      request.openingHours.clockFormat == null
    ) {
      request.openingHours = null;
    }

    if (request.id) {
      const updateResponse = await update({ id: request.id, request });
      return updateResponse.id;
    } else {
      const createResponse = await create(request);
      alert('Id of created brand: ' + createResponse.brandId);
      return createResponse.brandId;
    }
  }

  const isSubmitting = () => isUpdating || isCreating;

  async function onSaveBrand(model: Brand) {
    await SaveBrand(model);
    await refreshBrands();
  }

  const nameProps: InputProps<Brand> = {
    name: 'name',
    options: { required: 'Name is required.' },
    defaultValue: '',
    label: 'Name',
    type: 'text',
  };

  const urlProps: InputProps<Brand> = {
    name: 'url',
    options: { required: 'Url is required' },
    defaultValue: '',
    label: 'Url',
    type: 'text',
  };

  const emailAddressProps: InputProps<Brand> = {
    name: 'emailAddress',
    options: { required: 'Email address is required' },
    defaultValue: '',
    label: 'Email address',
    type: 'text',
  };

  const sortCodeProps: InputProps<Brand> = {
    name: 'sortCode',
    options: { required: 'Sort code is required' },
    defaultValue: '',
    label: 'Sort code',
    type: 'text',
  };

  const accountNumberProps: InputProps<Brand> = {
    name: 'accountNumber',
    options: { required: 'Account number is required' },
    defaultValue: '',
    label: 'Account Number',
    type: 'text',
  };

  const updateReasonProps: InputProps<Brand> = {
    name: 'updateReason',
    defaultValue: '',
    label: 'Update Reason',
    type: 'text',
    style: { visibility: isUpdateFlow ? 'visible' : 'hidden' },
  };
  const idProps: InputProps<Brand> = {
    name: 'id',
    defaultValue: '',
    label: 'Id',
    type: 'text',
    disabled: true,
    style: { visibility: isUpdateFlow ? 'visible' : 'hidden' },
  };

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    name: 'contactNumbers',
    control: formContext.control,
  });
  return (
    <FormProvider {...formContext}>
      <h2 className="ui dividing header">{brand ? 'Update Brand' : 'Add Brand'}</h2>
      <FormContainer onSubmit={onSaveBrand} isSubmitting={isSubmitting()} disableButtons>
        <Input {...nameProps} disabled />
        <Input {...urlProps} disabled />
        <Input {...emailAddressProps} disabled />
        <Input {...sortCodeProps} disabled />
        <Input {...accountNumberProps} disabled />
        <Input {...updateReasonProps} disabled />
        <Input {...idProps} disabled />
        <div className="sixteen wide column">
          <div className="sixteen wide column">
            <h3 className="ui horizontal divider header">
              <i className="phone icon"></i>
              Contact Numbers
            </h3>
            {contactFields.map((item, index) => {
              return (
                <div key={item.id}>
                  <div className="field">
                    <div className="ui labeled input">
                      <div className="ui label">Number</div>
                      <input
                        disabled
                        name={`contactNumbers[${index}].number`}
                        {...formContext.register(`contactNumbers.${index}.number` as const)}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <div className="ui labeled input">
                      <div className="ui label">Type</div>
                      <div className="ui input">
                        <select
                          disabled
                          name={`contactNumbers[${index}].type`}
                          {...formContext.register(`contactNumbers.${index}.type` as const)}>
                          <option>--Select a contact type--</option>
                          <option value="CustomerService">Customer Service</option>
                          <option value="Collection">Collection</option>
                          <option value="PersistentDebt">Persistent Debt</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <section>
                    <button
                      disabled
                      className="negative mini ui button"
                      type="button"
                      onClick={() => removeContact(index)}>
                      delete contact
                    </button>
                  </section>
                </div>
              );
            })}
          </div>
          <section>
            <h3 className="ui dividing header"></h3>
            <button
              disabled
              className="positive mini ui button"
              type="button"
              onClick={() => {
                appendContact({ name: '' });
              }}>
              add contact
            </button>
          </section>
        </div>

        <div className="sixteen wide column">
          <h3 className="ui horizontal divider header">
            <i className="clock outline icon"></i>
            Opening Hours
          </h3>
          <div className="field">
            <button
              className="ui icon button"
              type="button"
              onClick={() => {
                formContext.resetField('openingHours.clockFormat', { defaultValue: '--Select clock format--' });
                formContext.resetField('openingHours.customerService', { defaultValue: [] });
                formContext.resetField('openingHours.collection', { defaultValue: [] });
              }}
              disabled>
              <i className="eraser icon"></i>
              Delete Hours Configuration
            </button>
          </div>

          <div className="field">
            <div className="ui labeled input">
              <div className="ui label">Clock Format:</div>
              <select
                disabled
                name={`openingHours.clockFormat`}
                {...formContext.register(`openingHours.clockFormat` as const)}
                defaultValue={''}>
                <option>--Select clock format--</option>
                <option value="12hours">12hours</option>
                <option value="24hours">24hours</option>
              </select>
            </div>
          </div>
          <h4 className="ui dividing header">Customer Service</h4>
          <BrandOpeningHours
            collectionName={'customerService'}
            control={{ ...formContext.control }}
            register={{ ...formContext.register }}
            formContext={formContext}
            {...{ ...formContext.control, ...formContext.register }}
          />

          <h4 className="ui dividing header">Collection</h4>
          <BrandOpeningHours
            collectionName={'collection'}
            control={{ ...formContext.control }}
            register={{ ...formContext.register }}
            formContext={formContext}
            {...{ ...formContext.control, ...formContext.register }}
          />
        </div>
      </FormContainer>
    </FormProvider>
  );
}
